if (!customElements.get("set-product")) {
  class SetProduct extends HTMLElement {
    constructor() {
      super();

      this.options = [];
      this.variants = [];
      this.currentVariant = {};

      this.selectors = {
        variantsScript: '[type="application/json"]',
        allFields: 'input[type="radio"], select',
        selectedFields: 'input[type="radio"]:checked, select',
        swatch: ".swatch",
        productOptions: ".set-form__product-options",
        optionSelect: "[data-option-select]",
        optionLabel: ".js-option-label",
        preorderProperty: "data-preorder-property",
        deliveryDateProperty: "data-delivery-date-property",
      };

      document.addEventListener("set-product-selector:color-change", (event) =>
        this.#setColor(event.detail.color)
      );
    }

    /**
     * Update options based on checked/selected form fields.
     */
    #updateOptions = () => {
      this.options = Array.from(
        this.querySelectorAll(this.selectors.selectedFields),
        (el) => el.value
      );
    };

    /**
     * Update current variant based on selected options.
     */
    #updateVariant = () => {
      this.currentVariant = this.variants.find((variant) => {
        return !variant.options
          .map((option, index) => this.options[index] === option)
          .includes(false);
      });
    };

    /**
     * Update variant input based on currentVariant.
     */
    #updateVariantInput = () => {
      const input = this.querySelector("[name^=items]");
      input.value = this.currentVariant.id;
    };

    /**
     * Parse variants from script tag.
     */
    #parseVariants = () => {
      try {
        this.variants = JSON.parse(
          this.querySelector(this.selectors.variantsScript).innerHTML
        );
      } catch (error) {
        console.error(error);
      }
    };

    /**
     * Handle form field change.
     *
     * @param {Event} event
     */
    #handleFieldChange = (event) => {
      this.#updateOptions();
      this.#updateVariant();
      this.#updateVariantInput();
      this.#checkPreorderVariant();
      this.#setPrice(
        this.currentVariant?.price,
        this.#calculateDiscountedPrice()
      );
    };

    /**
     * Update image based on checked color input.
     *
     * @param {Event} event
     */
    #updateImage = (event) => {
      this.querySelectorAll("img").forEach((image) => {
        image.classList.toggle(
          "visible",
          image.getAttribute("color") === event.target.dataset.optionValue
        );
      });
    };

    /**
     * Attach events.
     */
    #attachEvents = () => {
      /**
       * On option change.
       */
      this.querySelectorAll(this.selectors.allFields).forEach((field) => {
        field.addEventListener("change", (event) => {
          this.#handleFieldChange(event);
          event.target
            .closest(this.selectors.productOptions)
            .querySelector(this.selectors.optionLabel).innerHTML =
            event.target.dataset.optionValue ||
            event.target.selectedOptions[0].value;

          this.dispatchEvent(
            new CustomEvent("set-product:variant-changed", {
              bubbles: true,
            })
          );
        });
      });

      /**
       * On color change.
       */
      this.querySelectorAll(this.selectors.swatch).forEach((swatch) => {
        swatch.previousElementSibling.addEventListener("change", (event) =>
          this.#updateImage(event)
        );
      });

      this.addEventListener("click", this.#handleClick.bind(this));
    };

    #handleClick = (event) => {
      const anchor = event.target.closest("a");

      if (!anchor) return;

      event.preventDefault();

      const url = anchor.getAttribute("href").split("?")[0] + "?view=set-item";

      fetch(url)
        .then((res) => res.text())
        .then((text) => {
          const index = this.getAttribute("data-index");
          const productHandle = this.closest("set-form").getAttribute("data-product-handle");

          const replacedText = text
            .replaceAll("[index]", index)
            .replaceAll("[index+1]", Number(index) + 1);

          const parser = new DOMParser();
          const parsedHTML = parser.parseFromString(replacedText, 'text/html');

          const setInput = parsedHTML.querySelector(`[name="items[${index}]properties[_set]"]`);
          const setBundleGroupInput = parsedHTML.querySelector(`[name="items[${index}]properties[_set-bundle-group]"]`);

          if (setInput) {
            setInput.value = productHandle;
          }

          if (setBundleGroupInput) {
            setBundleGroupInput.value = productHandle;
          }

          this.replaceChildren();

          Array.from(parsedHTML.body.children).forEach(child => {
            this.appendChild(child);
          });
        })
        .catch((error) => console.error(error));
    };

    /**
     * Disable swatches that don't exist in available variants.
     *
     * @returns {Void}
     */
    #setColorsAvailability = () => {
      const colorOptionPosition = this.querySelector(
        "[data-color-option-position]"
      )?.dataset.colorOptionPosition;

      if (!colorOptionPosition) return;

      const colors = this.querySelectorAll(".js-set-product-colors input");

      for (const color of colors) {
        // Check if there are any available variants with this color.
        const hasAvailable = this.variants
          .filter(
            (variant) => variant[`option${colorOptionPosition}`] === color.value
          )
          .some((variant) => variant.available);

        // Disable the swatch if there aren't any available variants with this color.
        !hasAvailable && color.setAttribute("disabled", true);
      }
    };

    /**
     * Disable select options that don't exist in available variants.
     *
     * @returns {Void}
     */
    #setSelectOptionsAvailability = () => {
      const selects = this.querySelectorAll(this.selectors.optionSelect);

      for (const select of selects) {
        const selectEl = select.querySelector("select");
        const selectOptionPosition = select.getAttribute(
          "data-option-position"
        );

        for (const option of selectEl.options) {
          // Check if there are any available variants with this option value.
          const hasAvailable = this.variants
            .filter(
              (variant) =>
                variant[`option${selectOptionPosition}`] === option.value
            )
            .some((variant) => variant.available);

          // Disable the option if there aren't any available variants with this value.
          !hasAvailable && option.setAttribute("disabled", true);
        }
      }
    };

    /**
     * Change selected color.
     *
     * @param {String} color
     */
    #setColor = (color) => {
      this.querySelector(
        `input[value="${color}"]`
      )?.nextElementSibling?.click();
    };

    /**
     * Select first available option.
     *
     * @return {void}
     */
    #setFirstAvailableOption = () => {
      const selects = this.querySelectorAll(this.selectors.optionSelect);

      for (const select of selects) {
        const selectEl = select.querySelector("select");
        selectEl.value = selectEl.querySelector("option:not(:disabled)")?.value;
      }
    };

    /**
     * Set price attributes.
     *
     * @param {Number} price
     * @param {Number} finalPrice
     */
    #setPrice = (price, finalPrice) => {
      this.setAttribute("original-price", price);
      this.setAttribute("final-price", finalPrice);
      this.dispatchEvent(
        new CustomEvent("set-product:price-changed", {
          bubbles: true,
        })
      );
    };

    /**
     * Calculate discounted price.
     *
     * @returns {Number}
     */
    #calculateDiscountedPrice = () => {
      return (
        this.currentVariant.price * (1 - Number(this.getAttribute("discount")))
      );
    };

    #checkPreorderVariant = () => {
      const { inventory_policy, inventory_quantity, next_available_date } =
        this.currentVariant;

      if (inventory_policy == "continue" && inventory_quantity <= 0) {
        this.setAttribute("data-preorder", true);
        this.setAttribute("data-preorder-date", next_available_date);
      } else {
        this.removeAttribute("data-preorder");
        this.removeAttribute("data-preorder-date");
      }

      this.#toggleProperties(
        inventory_policy == "continue" && inventory_quantity <= 0
      );
    };

    #toggleProperties(added = true) {
      if (added) {
        const nextAvailableDate = this.currentVariant.next_available_date;

        const inputNote = document.createElement("input");

        inputNote.type = "hidden";
        inputNote.name = `items[${this.dataset.index}]properties[Note]`;
        inputNote.value = window.cartStrings.preOrderDeliveryMessage.replace(
          "[date]",
          nextAvailableDate
        );
        inputNote.setAttribute(this.selectors.preorderProperty, true);

        this.appendChild(inputNote);

        const inputDeliveryDate = document.createElement("input");

        inputDeliveryDate.type = "hidden";
        inputDeliveryDate.name = `items[${this.dataset.index}]properties[_delivery_date]`;
        inputDeliveryDate.value = nextAvailableDate;
        inputDeliveryDate.setAttribute(
          this.selectors.deliveryDateProperty,
          true
        );

        this.appendChild(inputDeliveryDate);
      } else {
        if (this.querySelector(`[${this.selectors.preorderProperty}]`)) {
          this.querySelector(`[${this.selectors.preorderProperty}]`).remove();
        }

        if (this.querySelector(`[${this.selectors.deliveryDateProperty}]`)) {
          this.querySelector(
            `[${this.selectors.deliveryDateProperty}]`
          ).remove();
        }
      }
    }

    connectedCallback() {
      const variantsScript = this.querySelector(this.selectors.variantsScript);
      if (!variantsScript) return;

      this.#parseVariants();
      this.#attachEvents();
      this.#setColorsAvailability();
      this.#setSelectOptionsAvailability();
      // this.#setFirstAvailableOption();

      const mainProductSelectedColor = document.querySelector(
        ".product-selector__colors input:checked"
      )?.value;
      this.#setColor(mainProductSelectedColor);
    }
  }
  customElements.define("set-product", SetProduct);
}
